import { useEffect, useRef, useState } from 'react';
import { checkUsername, createAccount } from '../services/api';
import { getCopy } from '../utils/copy';

/**
 * 孵化后的起名结契:灵兽刚出生,一屏只问一件事 ——
 *
 *   ① 给灵兽起名        「我还没有名字呢,你给我起一个吧?」
 *   ② 主人名(= 账号名) 「那你叫什么名字呀?」 边输入边检查有没有人用
 *   ③ 暗号(= 密码)     「定一个只有你和我知道的暗号吧」
 *   ④ 结契卡片          「以后用『主人名 + 暗号』就能回来找我」
 *
 * 孩子是在回答灵兽的问题,不是在填注册表。规划见 docs/plan-003-account-rewards.md。
 */

// 按灵兽种类给几个候选名,不会起名的孩子点 🎲 就能往下走
export const NAME_SUGGESTIONS = {
  dragon: ['小澜', '阿渊', '沧沧', '小海'],
  monkey: ['悟悟', '石头', '小圣', '灵灵'],
  phoenix: ['小焰', '朱朱', '阿炎', '霞霞'],
  fusang: ['小桑', '芽芽', '青青', '阿木'],
  qilin: ['麟麟', '小祥', '安安', '土豆'],
};

const SPECIES_EMOJI = { dragon: '🐉', monkey: '🐵', phoenix: '🐦', fusang: '🌳', qilin: '🦄' };
const CHECK_DELAY_MS = 350;

function Bubble({ emoji, children }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <span className="text-7xl">{emoji}</span>
      <p className="relative rounded-2xl border-2 border-black bg-amber-50 px-4 py-3 text-center text-lg font-medium text-gray-900">
        {children}
      </p>
    </div>
  );
}

const primaryBtn =
  'w-full rounded-lg bg-qiji-gold px-6 py-3 text-base font-bold text-gray-900 border-2 border-black ' +
  'shadow-brutal-sm transition hover:-translate-y-0.5 active:translate-y-0 active:shadow-none ' +
  'disabled:opacity-40 disabled:pointer-events-none';
const inputCls =
  'w-full rounded-lg border-2 border-black px-4 py-3 text-center text-xl font-bold focus:outline-none';

const CRACK_MS = 700;

/**
 * returning=true:老存档凭恢复码找回后补设账号,开场白换成"好久不见"
 * hatching=true:刚选完蛋,先让孩子亲手点一点蛋、看它裂开,灵兽再开口
 */
function HatchAccountFlow({ sessionId, guardian, onDone, returning = false, hatching = false, ageBand }) {
  const emoji = SPECIES_EMOJI[guardian.species] || '🥚';
  const suggestions = NAME_SUGGESTIONS[guardian.species] || ['小宝'];

  const [step, setStep] = useState(hatching ? 'egg' : 'pet'); // egg | pet | owner | secret | pact
  const [cracking, setCracking] = useState(false);
  const crackTimer = useRef(null);
  useEffect(() => () => clearTimeout(crackTimer.current), []);

  const crackEgg = () => {
    if (cracking) return;
    setCracking(true);
    crackTimer.current = setTimeout(() => setStep('pet'), CRACK_MS);
  };
  const [petName, setPetName] = useState(guardian.name || '');
  const [owner, setOwner] = useState('');
  const [check, setCheck] = useState(null); // null | 'checking' | { available, reason, suggestions }
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const diceIndex = useRef(0);

  // 主人名:停止输入一小会儿后再查,避免每敲一个字就请求一次
  useEffect(() => {
    if (step !== 'owner' || !owner.trim()) {
      setCheck(null);
      return undefined;
    }
    setCheck('checking');
    let cancelled = false;
    const timer = setTimeout(() => {
      checkUsername(owner.trim())
        .then((res) => !cancelled && setCheck(res))
        .catch(() => !cancelled && setCheck({ available: false, reason: '网络有点慢,再试一次', suggestions: [] }));
    }, CHECK_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [owner, step]);

  const rollName = () => {
    setPetName(suggestions[diceIndex.current % suggestions.length]);
    diceIndex.current += 1;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await createAccount(sessionId, {
        username: owner.trim(),
        password: secret,
        guardianName: petName.trim(),
      });
      setResult(res);
      setStep('pact');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail === '这个名字已经有小伙伴用啦') {
        // 刚才还能用、这会儿被别人抢了:退回上一步(切回这一步会重新检查,给出候选名)
        setStep('owner');
        return;
      }
      setError(typeof detail === 'string' ? detail : '出了点小问题,再试一次吧');
    } finally {
      setSubmitting(false);
    }
  };

  const name = petName.trim();

  return (
    <div
      data-testid="hatch-account-flow"
      className="flex w-full max-w-sm flex-col items-center gap-5 rounded-2xl border-2 border-black bg-white p-6 shadow-brutal"
    >
      {step === 'egg' && (
        <div className="flex w-full flex-col items-center gap-4" data-testid="egg-step">
          <button
            type="button"
            aria-label="点一点灵兽蛋"
            onClick={crackEgg}
            className="h-28 w-28 text-8xl leading-none"
          >
            {/* 动画放在里面一层:按钮本身的位置不动,点起来不会"追着蛋点" */}
            <span className={`inline-block transition-transform ${cracking ? 'scale-125' : 'animate-bounce'}`}>
              {cracking ? '🐣' : '🥚'}
            </span>
          </button>
          <p className="rounded-2xl border-2 border-black bg-amber-50 px-4 py-3 text-center text-lg font-medium text-gray-900">
            {cracking ? '咔嚓……咔嚓……' : '蛋在动!轻轻点一点它吧'}
          </p>
        </div>
      )}

      {step === 'pet' && (
        <form
          className="flex w-full flex-col items-center gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name) setStep('owner');
          }}
        >
          <Bubble emoji={emoji}>
            {returning
              ? '好久不见!以后我们用名字和暗号相认吧 —— 先给我起个名字?'
              : '咿呀!我出生啦……可我还没有名字呢,你给我起一个吧?'}
          </Bubble>
          <div className="flex w-full gap-2">
            <input
              aria-label="灵兽的名字"
              value={petName}
              maxLength={10}
              placeholder={`比如:${suggestions[0]}`}
              onChange={(e) => setPetName(e.target.value)}
              className={inputCls}
            />
            <button
              type="button"
              onClick={rollName}
              aria-label="换一个名字"
              className="shrink-0 rounded-lg border-2 border-black bg-white px-3 text-2xl"
            >
              🎲
            </button>
          </div>
          <button type="submit" disabled={!name} className={primaryBtn}>
            就叫这个!
          </button>
        </form>
      )}

      {step === 'owner' && (
        <form
          className="flex w-full flex-col items-center gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (check?.available) {
              setError('');
              setStep('secret');
            }
          }}
        >
          <Bubble emoji={emoji}>{name}记住啦!那你叫什么名字呀?</Bubble>
          <input
            aria-label="主人名"
            value={owner}
            maxLength={12}
            placeholder="你的名字"
            onChange={(e) => setOwner(e.target.value)}
            className={inputCls}
          />
          <div className="min-h-[3rem] w-full text-center text-sm" aria-live="polite">
            {check === 'checking' && <p className="text-gray-400">看看这个名字有没有人用……</p>}
            {check && check !== 'checking' && check.available && (
              <p className="font-bold text-green-600">✓ 这个名字还没人用</p>
            )}
            {check && check !== 'checking' && !check.available && (
              <>
                <p className="text-qiji-coral">{check.reason}</p>
                {check.suggestions.length > 0 && (
                  <div className="mt-1 flex flex-wrap justify-center gap-1.5">
                    <span className="text-gray-500">试试</span>
                    {check.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setOwner(s)}
                        className="rounded-full border border-gray-400 px-2 text-gray-700"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {error && <p className="text-qiji-coral">{error}</p>}
          </div>
          <button type="submit" disabled={!check?.available} className={primaryBtn}>
            这就是我的名字
          </button>
          <button type="button" onClick={() => setStep('pet')} className="inline-flex min-h-[44px] items-center px-2 text-sm text-gray-600 underline">
            回去改{name}的名字
          </button>
        </form>
      )}

      {step === 'secret' && (
        <form className="flex w-full flex-col items-center gap-4" onSubmit={submit}>
          <Bubble emoji={emoji}>
            {owner.trim()},我们定一个只有你和我知道的暗号吧!下次你说出暗号,我就知道是你来啦。
          </Bubble>
          <div className="flex w-full gap-2">
            <input
              aria-label="暗号"
              type={showSecret ? 'text' : 'password'}
              value={secret}
              maxLength={64}
              placeholder="至少 4 个字"
              onChange={(e) => setSecret(e.target.value)}
              className={inputCls}
            />
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              aria-label={showSecret ? '藏起暗号' : '显示暗号'}
              className="shrink-0 rounded-lg border-2 border-black bg-white px-3 text-xl"
            >
              {showSecret ? '🙈' : '👁'}
            </button>
          </div>
          <p className="text-center text-sm text-gray-500">{getCopy(ageBand).codeHint}</p>
          {error && (
            <p role="alert" className="text-sm text-qiji-coral">
              {error}
            </p>
          )}
          <button type="submit" disabled={secret.length < 4 || submitting} className={primaryBtn}>
            {submitting ? '结契中……' : '定好啦'}
          </button>
          <button type="button" onClick={() => setStep('owner')} className="inline-flex min-h-[44px] items-center px-2 text-sm text-gray-600 underline">
            回去改我的名字
          </button>
        </form>
      )}

      {step === 'pact' && result && (
        <div className="flex w-full flex-col items-center gap-4" data-testid="pact-card">
          <span className="text-7xl">{emoji}</span>
          <div className="w-full rounded-xl border-2 border-dashed border-gray-500 bg-amber-50 px-4 py-4 text-center">
            <p className="text-2xl font-extrabold text-gray-900">{result.guardian.name}</p>
            <p className="mt-1 text-base text-gray-700">
              的主人:<b>{result.ownerName}</b>
            </p>
          </div>
          <div className="w-full rounded-xl border-2 border-black bg-amber-100 px-4 py-3 text-center">
            <p className="text-base font-extrabold text-gray-900">
              请记住:以后用「主人名 + 暗号」就能回来找我
            </p>
            <p className="mt-1 text-sm font-medium text-gray-800">
              换一台设备也可以。{getCopy(ageBand).codeHint}
            </p>
          </div>
          <button type="button" onClick={() => onDone(result)} className={primaryBtn}>
            出发冒险!
          </button>
        </div>
      )}
    </div>
  );
}

export default HatchAccountFlow;
